import { Localized } from "@fluent/react";
import { Alert, Box, Button, CardContent, CardHeader, Chip, LinearProgress, TextField, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import FormScreen from "./FormScreen";

export enum Feedback {
    RejectedNetwork,
    RejectedBug,
    RejectedInvalid,
    Accepted,
}

export enum ConfirmPasswordErrors {
    Mismatch,
}

interface DoneProps {
    feedback: Feedback,
}

export function Done(props: DoneProps) {
    return <FormScreen
        header={
            <Localized id="account-recovery-consume-header" attrs={{ title: true, subheader: true }}>
                <CardHeader />
            </Localized>
        }
        feedback={props.feedback && <FormFeedback feedback={props.feedback} />}
    >
        <CardContent>
            <Localized id="account-creation-continue-button">
                <Button
                    variant={props.feedback === Feedback.Accepted ? "contained" : "outlined"}
                    sx={{
                        margin: '16px auto 0px',
                        display: 'block',
                        width: 'fit-content',
                    }}
                    component={Link}
                    to="/"
                />
            </Localized>
        </CardContent>
    </FormScreen>;
}

export interface Field<E = undefined> {
    value: string;
    setValue(value: string): void;
    error?: E
}

export interface RecoverAccountUIProps {
    pending: boolean,
    passwordStrength: 0 | 1 | 2 | 3 | 4;
    feedback?: Feedback,
    email: string,
    password: string,
    confirmPassword: Field<ConfirmPasswordErrors>;
    
    setPassword(password: string): void,
    onSubmit?: () => void,
}

export default function RecoverAccountUI(props: RecoverAccountUIProps) {
    return <FormScreen
        pending={props.pending}
        header={
            <Localized id="account-recovery-consume-header" attrs={{ title: true, subheader: true }}>
                <CardHeader />
            </Localized>
        }
        feedback={props.feedback && <FormFeedback feedback={props.feedback} />}
        onSubmit={props.onSubmit}
    >
        <CardContent>
            <Localized
                id="account-recovery-consume-password-description">
                <Typography />
            </Localized>
            <Box sx={{ margin: '16px 0 8px 0' }}>
                <Chip icon={<AccountCircleIcon />} label={props.email} />
            </Box>
            <Localized id="account-recovery-consume-password" attrs={{ label: true }}>
                <TextField
                    type="password"
                    value={props.password}
                    onChange={e => props.setPassword(e.target.value)}
                    disabled={props.pending}
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth />
            </Localized>
            <Localized id="account-creation-confirm-password"
                    attrs={{
                        label: true,
                        helperText: props.confirmPassword.error === ConfirmPasswordErrors.Mismatch
                    }}>
                    <TextField
                        type="password"
                        value={props.confirmPassword.value}
                        onChange={e => props.confirmPassword.setValue(e.target.value)}
                        disabled={props.pending}
                        variant="outlined"
                        margin="normal"
                        required
                        error={props.confirmPassword.error === ConfirmPasswordErrors.Mismatch}
                        fullWidth />
                </Localized>
                <Localized id="account-creation-confirm-password-description">
                    <Typography />
                </Localized>

        </CardContent>

        <CardContent sx={{ borderTop: '1px #c4c4c4 solid' }}>
            {props.password && <Box sx={{ marginBottom: '16px' }}>
                <Localized id="account-creation-password-strength">
                    <Typography variant="h5" />
                </Localized>
                <LinearProgress
                    variant="determinate"
                    value={(props.passwordStrength + 1) / 5 * 100}
                    color={
                        props.passwordStrength === 0 ? 'error' :
                        props.passwordStrength === 1 ? 'error' :
                        props.passwordStrength === 2 ? 'warning' :
                        props.passwordStrength === 3 ? 'success' :
                        props.passwordStrength === 4 ? 'success' :
                        undefined
                    }
                    sx={{ height: "8px" }}
                />
                {props.passwordStrength <= 2 &&
                    <Localized id="account-creation-password-too-weak">
                        <Typography variant="subtitle2" />
                    </Localized>
                }
            </Box>}

            <Localized id="account-recovery-consume-submit-button">
                <Button
                    sx={{
                        margin: '0 auto',
                        display: 'block'
                    }}
                    type="submit"
                    disabled={props.pending || !props.onSubmit}
                    variant="contained"
                />
            </Localized>
        </CardContent>
    </FormScreen>
}

interface FormFeedbackProps {
	feedback: Feedback;
}

function formFeedbackMsg(feedback: Feedback): {stringID: string, severity: "error" | "success"} {
	switch (feedback) {
		case Feedback.RejectedBug:
			return {
				stringID: "account-recovery-consume-feedback-bug",
				severity: "error",
			};
		case Feedback.RejectedNetwork:
			return {
				stringID: "account-recovery-consume-feedback-network",
				severity: "error",
			};
        case Feedback.RejectedInvalid:
			return {
				stringID: "account-recovery-consume-feedback-invalid",
				severity: "error",
			};
		case Feedback.Accepted:
			return {
				stringID: "account-recovery-consume-feedback-success",
				severity: "success",
			};
	}
}

function FormFeedback(props: FormFeedbackProps) {
	const response = formFeedbackMsg(props.feedback);

	return (
		<Localized id={response.stringID}>
			<Alert severity={response.severity} />
		</Localized>
	);
}
